"use client";

import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { CaretSortIcon } from "@radix-ui/react-icons";
import { XMarkIcon, CheckIcon } from "@heroicons/react/24/outline";
import { memo, useState } from "react";

import { Hidden } from "@/components/ui/hidden";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import { cn } from "@/lib/utils";
import { defaultEmptyText } from "@/constants";
import { filterSearchParam } from "@/filters";
import { useFilter } from "../use-filter";

import type { House, HouseCityParam, PropertiesCitiesCount } from "@/types";
import { usePropertiesUfCities } from "@/queries/use-properties";

type CitiesFilter = PropertiesCitiesCount["cities"];

interface SearchCityProps {
  filter: House["uf"];
  action: {
    filter: (page: HouseCityParam["pagina"]) => void;
    clean: () => void;
  };
}

const List = ({
  isPending,
  filter,
  cities,
  setSearchParam,
}: {
  isPending: boolean;
  filter: CitiesFilter;
  cities: string[];
  setSearchParam: any;
}) => {
  return (
    <Command>
      <CommandInput placeholder="Buscar cidade..." />
      <CommandList className="px-1">
        <CommandEmpty>{defaultEmptyText}</CommandEmpty>
        <CommandGroup heading="Cidades">
          {isPending ? (
            <CommandItem>Carregando...</CommandItem>
          ) : (
            filter.map((key, index) => {
              return (
                <CommandItem
                  key={`${key.value}-${index}`}
                  onSelect={() => {
                    const length = cities.length;

                    if (length) {
                      const isSelected = cities.includes(key.value);

                      if (length === 1 && isSelected) {
                        return setSearchParam({
                          [filterSearchParam.cidade]: null,
                        });
                      }

                      setSearchParam({
                        [filterSearchParam.cidade]: isSelected
                          ? cities.filter((item) => item !== key.value)
                          : [...cities, key.value],
                      });
                    } else {
                      setSearchParam({
                        [filterSearchParam.cidade]: [key.value],
                      });
                    }
                  }}
                >
                  {key.value} ({key.count})
                  <CheckIcon
                    className={cn(
                      "ml-auto size-4",
                      cities.includes(key.value) ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              );
            })
          )}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};

export const SearchCity = memo(({ action, filter }: SearchCityProps) => {
  const [{ cidade }, setSearchParam] = useFilter();
  const [visible, setVisible] = useState(false);
  const { data, isPending } = usePropertiesUfCities({ uf: filter });

  const hasCities = cidade.length > 0;

  const cities = data?.cities || [];

  const open = () => {
    setVisible(true);
  };

  const close = () => {
    setVisible(false);
  };

  const fetchFilter = () => {
    action.filter(1);
    close();
  };

  const cleanFilter = () => {
    action.clean();
    close();
  };

  return (
    <div className="flex items-center gap-2 w-full sm:w-auto">
      <Hidden
        content={[
          <Sheet key="mobile">
            <SheetTrigger asChild>
              <Button
                variant="outline"
                aria-expanded={visible}
                className="flex-1 justify-between w-full whitespace-normal"
              >
                {hasCities ? cidade.join(", ") : "Todas as cidades"}
                <CaretSortIcon className="ml-2 size-4 shrink-0 opacity-50" />
              </Button>
            </SheetTrigger>
            <SheetContent
              aria-describedby={undefined}
              side="bottom"
              className="bg-white flex flex-col gap-10 overflow-auto rounded-tl-md rounded-tr-md w-full p-0 min-h-[70%] max-h-[70%]"
            >
              <VisuallyHidden asChild>
                <SheetTitle>Cidades</SheetTitle>
              </VisuallyHidden>
              <div className="w-full flex flex-col gap-2 p-5 sm:p-7">
                <div className="flex items-center justify-end">
                  <SheetClose asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      aria-label="Fechar"
                      className="p-2"
                    >
                      <XMarkIcon className="size-4" aria-hidden="true" />
                    </Button>
                  </SheetClose>
                </div>
                <List
                  isPending={isPending}
                  filter={cities}
                  cities={cidade}
                  setSearchParam={setSearchParam}
                />
                <div className="flex items-center justify-end gap-2 sticky bottom-0 bg-white py-4 z-20">
                  <SheetClose asChild>
                    <Button variant="ghost" onClick={cleanFilter}>
                      Limpar
                    </Button>
                  </SheetClose>
                  <SheetClose asChild>
                    <Button onClick={fetchFilter}>Filtrar</Button>
                  </SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>,
          <Popover key="desktop" open={visible} onOpenChange={setVisible}>
            <PopoverTrigger asChild onClick={open}>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={visible}
                className="flex-1 justify-between w-full"
              >
                {hasCities ? cidade.join(", ") : "Todas as cidades"}
                <CaretSortIcon className="ml-2 size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0">
              <List
                isPending={isPending}
                filter={cities}
                cities={cidade}
                setSearchParam={setSearchParam}
              />
              <div className="flex items-center justify-end gap-2 p-3">
                <Button variant="ghost" onClick={cleanFilter}>
                  Limpar
                </Button>
                <Button onClick={fetchFilter}>Filtrar</Button>
              </div>
            </PopoverContent>
          </Popover>,
        ]}
      />
      {hasCities ? (
        <Button
          variant="outline"
          size="sm"
          aria-label="Limpar filtros"
          className="p-2"
          onClick={cleanFilter}
        >
          <XMarkIcon className="size-4" aria-hidden="true" />
        </Button>
      ) : null}
    </div>
  );
});

SearchCity.displayName = "SearchCity";
